import {
  Alert,
  Button,
  Empty,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useFfsDisplayUnits } from 'features/indicationTriage/cards/ffsCalc/useFfsDisplayUnits'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  buildFinalResult,
  FFS_FINAL_RESULT_FIELD,
  FfsFinalResult,
} from 'features/libra/stepComponents/ffs/ffsFinalResult'
import {
  LtaUnassessedReason,
  progressOf,
  ScopeRollup,
} from 'features/libra/stepComponents/ffs/ffsResultRollup'
import { isFfsResultType } from 'features/libra/stepComponents/ffs/ffsSeverity'
import {
  ComponentRow,
  LtaRow,
} from 'features/libra/stepComponents/ffs/ffsUnitAssembly'
import {
  canRecordResult,
  useFfsUnitRollup,
} from 'features/libra/stepComponents/ffs/useFfsUnitRollup'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { LibraWorflowStepComponentProps } from 'features/libra/workflow/workflowStepsApi'
import { useGeckoPermissions } from 'features/permissions'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

/**
 * FFS Close-Out & Review — the final step of the FFS workflow.
 *
 * Fitness-for-service is decided by the worst case, so this aggregates every
 * LTA's verdict into a per-component verdict and then into one unit verdict, and
 * records it on the ticket. Unit-wide by design: it must NOT be registered with
 * `requiresComponentScope`, because it reads across all components at once.
 *
 * All judgement lives in `ffsUnitAssembly` / `ffsResultRollup`; this file only
 * renders and writes.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`

const Title = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
`

const Blurb = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const ScenarioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const VerdictBanner = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border-left: 6px solid ${({ $color }) => $color};
  background-color: ${({ theme }) => theme.forecast.background2};
`

const VerdictLabel = styled.span<{ $color: string }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
`

const VerdictDetail = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const Muted = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const LtaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.5rem 0;
`

const LtaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-size: 0.8125rem;
`

const TABLE_LOCALE = {
  emptyText: (
    <Empty
      description='No components with inspections on this unit'
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  ),
}

/** Why a scope shows no verdict, in the engineer's words. */
const SCOPE_STATE_COPY: Record<
  Exclude<ScopeRollup['kind'], 'verdict' | 'incomplete'>,
  string
> = {
  'not-started': 'Binning has not been run',
  'no-ltas-found': 'No LTAs found',
  'no-verdict': 'All LTAs skipped',
}

const UNASSESSED_COPY: Record<LtaUnassessedReason, string> = {
  'no-classification': 'Not classified — complete FFS Pre-Planning',
  'unparseable-indication': 'LTA record could not be read',
  unparseable: 'A saved report could not be read',
  'no-report': 'No assessment saved',
  'not-in-scenario': 'Not assessed in this scenario',
  'no-usable-scenario': 'Saved reports have no target date or scenario name',
  pending: 'Assessment still in progress',
  ambiguous: 'Conflicting saved reports',
  stale: 'Assessed before the LTA was re-classified',
  'needs-resave': 'Saved before an update — re-save the assessment',
  'ineligible-level':
    'Assessed at a level not eligible for this component type',
}

/** The `<Tag>` for an FFS outcome — one home for the color+label pairing. */
const ResultTag: FC<{ result: FfsResultType }> = ({ result }) => (
  <Tag color={RESULT_OUTCOME_COLORS[result]}>
    {RESULT_OUTCOME_LABELS[result]}
  </Tag>
)

/** The one-off tag color for "the governing report is not the flagged default". */
const DIVERGENCE_TAG_COLOR = 'gold'

const scopeTag = (rollup: ScopeRollup) => {
  if (rollup.kind === 'verdict') {
    return <ResultTag result={rollup.result} />
  }
  if (rollup.kind === 'incomplete') {
    const progress = progressOf(rollup.counts)
    return (
      <Tag>
        {progress
          ? `Pending — ${progress.assessed} of ${progress.expected} assessed`
          : 'Pending'}
      </Tag>
    )
  }
  return <Tag>{SCOPE_STATE_COPY[rollup.kind]}</Tag>
}

const derateText = (
  rollup: ScopeRollup,
  pressureUnit: GeckoMeasurementUnit
): string | null => {
  if (rollup.kind !== 'verdict' || rollup.result !== FfsResultType.Derate) {
    return null
  }
  if (rollup.derate?.kind === 'known') {
    const { mawp } = rollup.derate
    // Display in the preferred pressure unit; the stored value is unchanged.
    const shown = mawp.convertTo(pressureUnit)
    return `Derate to ${shown.num.toFixed(3).replace(/\.?0+$/, '')} ${shown.unit.name}`
  }
  return 'Derate — pressure unavailable'
}

const LtaDetail = memo(({ lta }: { lta: LtaRow }) => {
  const { disposition } = lta
  const route =
    lta.governingPart && lta.governingLevel
      ? `${lta.governingPart === 'part-4' ? 'Part 4' : 'Part 5'} Level ${lta.governingLevel}`
      : null

  return (
    <LtaLine>
      <strong>{lta.title}</strong>
      {disposition.kind === 'verdict' && (
        <ResultTag result={disposition.result} />
      )}
      {disposition.kind === 'skipped' && (
        <ResultTag result={FfsResultType.Skipped} />
      )}
      {disposition.kind === 'unassessed' && (
        <Muted>{UNASSESSED_COPY[disposition.reason]}</Muted>
      )}
      {/* Type C fails on its classification alone — say so, rather than letting
          it read as "failed the criteria". */}
      {disposition.kind === 'verdict' &&
        disposition.reason === 'not-assessable' && (
          <Muted>
            Not demonstrable at Level 1/2 — Level 3 or repair required
          </Muted>
        )}
      {route && <Muted>· governed by {route}</Muted>}
      {disposition.kind === 'verdict' && disposition.disagreesWithDefault && (
        <Tooltip title='The report flagged as default in the LTA drawer is not the one that governs here. Close-out never changes that flag.'>
          <Tag color={DIVERGENCE_TAG_COLOR}>Differs from flagged default</Tag>
        </Tooltip>
      )}
      {disposition.kind === 'verdict' && disposition.timestampUnusable && (
        <Tooltip title='A timestamp could not be read, so this report could not be checked against its classification date.'>
          <Tag>Age unverified</Tag>
        </Tooltip>
      )}
    </LtaLine>
  )
})
LtaDetail.displayName = 'LtaDetail'

/** The expanded-row body for one component: its LTA lines plus the set-age note. */
const ComponentLtaList = memo(({ row }: { row: ComponentRow }) => (
  <LtaList>
    {row.ltas.length === 0 ? (
      <Muted>No LTAs in this set.</Muted>
    ) : (
      row.ltas.map((lta) => <LtaDetail key={lta.ltaId} lta={lta} />)
    )}
    {row.ltaSetUpdatedAt && (
      <Muted>
        LTA set {row.ltaSetId} last changed {row.ltaSetUpdatedAt}
      </Muted>
    )}
  </LtaList>
))
ComponentLtaList.displayName = 'ComponentLtaList'

/** The unit-level verdict banner. Localizes its own color derivation. */
const UnitVerdictBanner: FC<{ rollup: ScopeRollup }> = ({ rollup }) => {
  const theme = useTheme()
  const { pressure: pressureUnit } = useFfsDisplayUnits()

  if (rollup.kind === 'verdict') {
    const color = RESULT_OUTCOME_COLORS[rollup.result]
    return (
      <VerdictBanner $color={color}>
        <VerdictLabel $color={color}>
          {RESULT_OUTCOME_LABELS[rollup.result]}
        </VerdictLabel>
        <VerdictDetail>
          {derateText(rollup, pressureUnit) ??
            'Final fitness-for-service result for this unit'}
        </VerdictDetail>
      </VerdictBanner>
    )
  }

  const color = theme.forecast.tertiaryText
  return (
    <VerdictBanner $color={color}>
      <VerdictLabel $color={color}>
        {rollup.kind === 'no-verdict' ? 'Not applicable' : 'Pending'}
      </VerdictLabel>
      <VerdictDetail>
        {rollup.kind === 'no-verdict'
          ? 'No component produced an assessable verdict.'
          : 'One or more components still need work — an incomplete assessment makes no claim.'}
      </VerdictDetail>
    </VerdictBanner>
  )
}

const buildColumns = (
  pressureUnit: GeckoMeasurementUnit
): ColumnsType<ComponentRow> => [
  { title: 'Component', dataIndex: 'componentName', key: 'name' },
  {
    title: 'Status',
    key: 'status',
    render: (_, row) => scopeTag(row.rollup),
  },
  {
    title: 'LTAs',
    key: 'counts',
    render: (_, row) => {
      if (!('counts' in row.rollup)) return <Muted>—</Muted>
      const { total, skipped, notAssessable } = row.rollup.counts
      return (
        <span>
          {total}
          {skipped > 0 && <Muted> · {skipped} skipped</Muted>}
          {notAssessable > 0 && (
            <Muted> · {notAssessable} not assessable</Muted>
          )}
        </span>
      )
    },
  },
  {
    title: 'Re-rate',
    key: 'derate',
    render: (_, row) =>
      derateText(row.rollup, pressureUnit) ?? <Muted>—</Muted>,
  },
]

const EXPANDABLE = {
  expandedRowRender: (row: ComponentRow) => <ComponentLtaList row={row} />,
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = ({ readonly = false }) => {
  const rollup = useFfsUnitRollup()
  const {
    scenarios,
    selectedScenario,
    setSelectedScenario,
    selectionFellBack,
    unitRollup,
    componentRows,
    excludedComponents,
    status,
    blockers,
  } = rollup

  const { ticketTags, fieldValues, setFieldValue } = useTicketAccess()
  const { user } = useGeckoPermissions()
  const [isSaving, setIsSaving] = useState(false)

  // Re-rate pressures render in the preferred unit (stored values unchanged).
  const { pressure: pressureUnit } = useFfsDisplayUnits()
  const columns = useMemo(() => buildColumns(pressureUnit), [pressureUnit])

  // Read through the real persisted type rather than an ad-hoc cast that
  // re-declares half of it and would drift from the writer.
  const recorded = unwrapFieldValue<FfsFinalResult>(
    fieldValues[FFS_FINAL_RESULT_FIELD]
  )

  const canRecord = canRecordResult(rollup)

  // D3: the step is not complete until the engineer has recorded a result.
  // Deliberately NOT a live-vs-recorded match check -- a later data change shows
  // a notice but must not re-open a signed-off ticket.
  useReportValidation(
    useMemo(
      () =>
        recorded
          ? undefined
          : {
              status: 'Error' as const,
              message: blockers.length
                ? blockers[0].detail
                : 'Record the final FFS result to complete this step.',
            },
      [recorded, blockers]
    )
  )

  // Derived to a stable string so `handleRecord` does not churn on the `user`
  // object's identity — only when the name/email it actually reads change.
  const decidedBy = user?.name || user?.email || 'Unknown User'

  const handleRecord = useCallback(async () => {
    const payload = buildFinalResult({
      unitId: ticketTags.unit ?? '',
      scenarioKey: selectedScenario,
      decidedBy,
      decidedAt: new Date().toISOString(),
      unitRollup,
      componentRows,
      excludedComponents,
    })
    if (!payload) return
    setIsSaving(true)
    try {
      await setFieldValue(FFS_FINAL_RESULT_FIELD, wrapFieldValue(payload))
    } finally {
      setIsSaving(false)
    }
  }, [
    ticketTags.unit,
    selectedScenario,
    decidedBy,
    unitRollup,
    componentRows,
    excludedComponents,
    setFieldValue,
  ])

  const scenarioOptions = useMemo(
    () =>
      scenarios.map((s) => ({
        value: s.key,
        label: `${s.label} (${s.coverage} LTA${s.coverage === 1 ? '' : 's'})`,
      })),
    [scenarios]
  )

  const dataSource = useMemo(() => [...componentRows], [componentRows])

  if (status === 'loading') return <Skeleton active paragraph={{ rows: 8 }} />

  return (
    <Container>
      <Header>
        <div>
          <Title>FFS Close-Out &amp; Review</Title>
          <Blurb>
            The unit&apos;s result is the most conservative outcome across its
            components, and each component&apos;s is the most conservative
            across its LTAs.
          </Blurb>
        </div>
        <ScenarioRow>
          <span>Scenario:</span>
          <Select
            aria-label='Scenario'
            style={{ minWidth: 260 }}
            value={selectedScenario ?? undefined}
            onChange={setSelectedScenario}
            disabled={readonly || scenarios.length === 0}
            placeholder='No saved scenarios'
            options={scenarioOptions}
          />
        </ScenarioRow>
      </Header>

      {blockers.map((blocker) => (
        <Alert
          key={blocker.kind}
          type='error'
          showIcon
          message={blocker.detail}
        />
      ))}

      {selectionFellBack && (
        <Alert
          type='warning'
          showIcon
          message='The scenario you picked is no longer present in the saved reports. Showing the best-covered scenario instead.'
        />
      )}

      <UnitVerdictBanner rollup={unitRollup} />

      {/* The plan must not imply a guarantee the code cannot make: close-out
          cannot tell whether LTAs were re-binned after they were assessed. */}
      {componentRows.some((r) => r.ltaSetUpdatedAt) && (
        <Alert
          type='info'
          showIcon
          message='LTA sets are not version-tracked. If LTAs were regenerated after an assessment was saved, close-out cannot detect it — check the LTA set dates below against your assessments.'
        />
      )}

      <Table<ComponentRow>
        rowKey='componentId'
        size='small'
        pagination={false}
        columns={columns}
        dataSource={dataSource}
        locale={TABLE_LOCALE}
        expandable={EXPANDABLE}
      />

      {excludedComponents.length > 0 && (
        <Alert
          type='warning'
          showIcon
          message={`${excludedComponents.length} component(s) have no inspection and are excluded from this result: ${excludedComponents
            .map((c) => c.name)
            .join(', ')}`}
        />
      )}

      {recorded && (
        <Alert
          type='success'
          showIcon
          message={`Recorded: ${
            isFfsResultType(recorded.result)
              ? RESULT_OUTCOME_LABELS[recorded.result]
              : 'Not applicable'
          }${
            recorded.scenario.targetDate
              ? ` for target ${recorded.scenario.targetDate}`
              : ''
          }`}
        />
      )}

      <div>
        <Tooltip
          title={
            canRecord
              ? undefined
              : 'Every component must reach a verdict, or be explicitly not applicable, before the result can be recorded.'
          }>
          <Button
            type='primary'
            loading={isSaving}
            disabled={readonly || !canRecord}
            onClick={handleRecord}>
            {recorded ? 'Update final result' : 'Record final result'}
          </Button>
        </Tooltip>
      </div>
    </Container>
  )
}
